/**
 * ProtocolWizard — Assistent für Übergabe/Rücknahme/Schlüssel/Begehung etc.
 *
 * 5 Schritte (dynamisch, je nach Typ):
 *   1. Typ & Termin & Beteiligte
 *   2. Räume & Mängel     (nur bei handover_in, handover_out, inspection, interim_inspection)
 *   3. Zähler             (nur bei handover_in, handover_out)
 *   4. Schlüssel & Dokumente
 *   5. Bemerkungen & Unterschriften → finalisieren (SHA-256 Signatur) ODER PDF-Ausdruck
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash, Printer, FloppyDisk, Signature, ArrowLeft, ArrowRight, Camera, X, ClipboardText } from "@phosphor-icons/react";
import SignatureCanvas from "@/components/SignatureCanvas";

const TYPE_LABELS = {
  handover_in: "Übergabeprotokoll (Einzug)",
  handover_out: "Rücknahmeprotokoll (Auszug)",
  key_handover: "Schlüsselübergabeprotokoll",
  inspection: "Begehungsprotokoll",
  document_handover: "Dokumentenübergabeprotokoll",
  interim_inspection: "Zwischenabnahmeprotokoll",
  trade_acceptance: "Baugewerke-Abnahmeprotokoll",
};

const HAS_ROOMS = new Set(["handover_in", "handover_out", "inspection", "interim_inspection"]);
const HAS_METERS = new Set(["handover_in", "handover_out"]);
const HAS_KEYS = new Set(["handover_in", "handover_out", "key_handover"]);
const HAS_DOCS = new Set(["document_handover", "handover_in"]);

const emptyProtocol = (initial = {}) => ({
  protocol_type: "handover_in",
  status: "draft",
  tenancy_id: null,
  property_id: null,
  unit_id: null,
  appointment_date: new Date().toISOString().slice(0, 10),
  appointment_time: "",
  location: "",
  general_notes: "",
  participants: [
    { role: "manager", name: "" },
    { role: "tenant", name: "" },
  ],
  rooms: [],
  meters: [],
  keys: [],
  documents_transferred: [],
  global_photos_base64: [],
  ...initial,
});

async function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function ProtocolWizard({ open, onOpenChange, initial = {}, onSaved, lockTenancy = false }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [protocolId, setProtocolId] = useState(initial.id || null);
  const [form, setForm] = useState(emptyProtocol(initial));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm(emptyProtocol(initial));
      setProtocolId(initial.id || null);
      setLastSavedAt(null);
      setStep(1);
    }
  }, [open, initial]);

  // 2026-08 (iter104) — Zähler aus dem verknüpften Objekt/Einheit automatisch
  // vorbelegen, wenn (a) das Wizard-Fenster frisch geöffnet wird, (b) noch
  // keine Meters gesetzt sind und (c) das Protokoll noch nicht gespeichert wurde
  // (also frischer Entwurf). Der Nutzer kann jederzeit weitere manuell hinzufügen.
  useEffect(() => {
    if (!open) return;
    if (protocolId) return;                 // Kein Auto-Prefill für bereits gespeicherte Protokolle
    if ((initial.meters || []).length) return;  // Wenn caller schon welche mitgab
    const pid = initial.property_id || form.property_id;
    if (!pid) return;
    if (!HAS_METERS.has(form.protocol_type)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: prop } = await api.get(`/properties/${pid}`);
        const uid = initial.unit_id || form.unit_id;
        const propMeters = (prop?.meters || []).filter(
          (m) => !uid || !m.unit_id || m.unit_id === uid,
        );
        if (!propMeters.length || cancelled) return;
        // Nur vorbelegen wenn der Nutzer noch nichts eingetragen hat
        setForm((s) => {
          if ((s.meters || []).length) return s;
          return {
            ...s,
            meters: propMeters.map((m) => ({
              kind: m.meter_type || "other",
              label: m.location || "",
              meter_number: m.meter_number || "",
              reading: "",
              unit: m.measurement_unit || "",
              linked_meter_id: m.id,
              linked: true,
            })),
          };
        });
        toast.info(`${propMeters.length} verknüpfte${propMeters.length === 1 ? "r" : ""} Zähler vorbelegt`);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.property_id, form.unit_id, form.protocol_type, protocolId]);

  // 2026-02-24 — Räume aus Einheiten-Stammdaten (`/units/{uid}/rooms`) vorbelegen,
  // damit sich Übergabe-/Rücknahme-/Begehungs-Protokolle blind auf die einmal
  // gepflegte Raum-Liste stützen. Der Nutzer kann jederzeit weitere Räume
  // ergänzen oder Änderungen später über den „Wiederherstellen"-Button einlesen.
  useEffect(() => {
    if (!open) return;
    if (protocolId) return;
    if ((initial.rooms || []).length) return;
    const uid = initial.unit_id || form.unit_id;
    if (!uid) return;
    if (!HAS_ROOMS.has(form.protocol_type)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: rooms } = await api.get(`/units/${uid}/rooms`);
        if (!rooms || !rooms.length || cancelled) return;
        setForm((s) => {
          if ((s.rooms || []).length) return s;
          return {
            ...s,
            rooms: rooms.map((r) => ({
              name: r.label || "Raum",
              condition_notes: r.condition_notes || "",
              photos_base64: [],
              defects: [],
              linked_room_id: r.id,
            })),
          };
        });
        toast.info(`${rooms.length} Raum${rooms.length === 1 ? "" : "e"} aus der Einheit vorbelegt`);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.unit_id, form.protocol_type, protocolId]);

  // Übergabe/Rücknahme: Termin, Treffpunkt und Mieter aus dem Mietverhältnis vorbelegen
  useEffect(() => {
    if (!open || protocolId || !form.tenancy_id) return;
    const kind = { handover_in: "in", handover_out: "out" }[form.protocol_type];
    if (!kind) return;
    let cancelled = false;
    api.get(`/handover-appointments/tenancy/${form.tenancy_id}`, { params: { kind } }).then(({ data }) => {
      if (cancelled || !data.date) return;
      setForm((s) => {
        const others = (s.participants || []).filter((p) => !(p.role === "tenant" && !p.name));
        const known = new Set(others.map((p) => p.name));
        const tenants = (data.tenants || []).filter((n) => !known.has(n)).map((name) => ({ role: "tenant", name }));
        return {
          ...s,
          appointment_date: initial.appointment_date || data.date,
          appointment_time: initial.appointment_time || data.time || s.appointment_time,
          location: initial.location || data.location || s.location,
          participants: [...others, ...tenants],
        };
      });
      toast.info("Termin, Treffpunkt und Mieter aus dem Mietverhältnis übernommen");
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.tenancy_id, form.protocol_type, protocolId]);

  // 2026-08 — Auto-Save: 1,5 s Debounce. Erste Änderung erzeugt einen Entwurf,
  // spätere Änderungen aktualisieren ihn. Signaturen + Fotos werden mitgespeichert.
  useEffect(() => {
    if (!open) return;
    // Kein Autosave beim ersten Rendern (initial state), nur nach echten Änderungen
    if (!form.protocol_type) return;
    const handle = setTimeout(async () => {
      try {
        setAutoSaving(true);
        if (protocolId) {
          await api.put(`/protocols/${protocolId}`, form);
        } else {
          // Nur speichern wenn der Nutzer wirklich Inhalt hinzugefügt hat
          const meaningful = form.location || form.appointment_date ||
                             (form.participants || []).some((p) => p.name) ||
                             (form.rooms || []).length > 0 || (form.meters || []).length > 0 ||
                             (form.keys || []).length > 0;
          if (!meaningful) return;
          const { data } = await api.post("/protocols", form);
          setProtocolId(data.id);
          onSaved?.(data.id);
        }
        setLastSavedAt(new Date());
      } catch { /* stille Autosaves — Fehler beim manuellen Save gemeldet */ }
      finally { setAutoSaving(false); }
    }, 1500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, open]);

  const activeSteps = [
    "Termin & Beteiligte",
    HAS_ROOMS.has(form.protocol_type) && "Räume & Mängel",
    HAS_METERS.has(form.protocol_type) && "Zählerstände",
    (HAS_KEYS.has(form.protocol_type) || HAS_DOCS.has(form.protocol_type)) && "Schlüssel & Dokumente",
    "Unterschriften & Abschluss",
  ].filter(Boolean);

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      let id = protocolId;
      if (id) {
        await api.put(`/protocols/${id}`, payload);
      } else {
        const { data } = await api.post("/protocols", payload);
        id = data.id;
        setProtocolId(id);
      }
      toast.success("Entwurf gespeichert");
      onSaved?.(id);
      return id;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openPdfInNewTab = async () => {
    const id = protocolId || await saveDraft();
    if (!id) return;
    const url = `${api.defaults.baseURL || ""}/protocols/${id}/preview-pdf`;
    const { data } = await api.get(`/protocols/${id}/preview-pdf`, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(data);
    window.open(blobUrl, "_blank");
  };

  const finalize = async () => {
    const id = protocolId || await saveDraft();
    if (!id) return;
    setSaving(true);
    try {
      await api.put(`/protocols/${id}`, form);
      await api.post(`/protocols/${id}/sign`);
      toast.success("Protokoll finalisiert und mit Prüfsumme abgesichert.");
      onSaved?.(id, { finalized: true });
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const canProceed = step < activeSteps.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="protocol-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardText size={20} className="text-emerald-600" />
            {TYPE_LABELS[form.protocol_type] || "Protokoll"}
            <span className="text-xs font-normal text-slate-400 ml-2">
              Schritt {step}/{activeSteps.length}: {activeSteps[step - 1]}
            </span>
            <span className="ml-auto text-[10px] text-slate-400 italic tabular-nums" data-testid="wizard-autosave-hint">
              {autoSaving ? "speichere…" : lastSavedAt ? `zuletzt gespeichert ${lastSavedAt.toLocaleTimeString("de-DE").slice(0, 5)}` : "Entwurf wird automatisch gespeichert"}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex gap-1 mb-3">
          {activeSteps.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>Protokolltyp</Label>
              <select value={form.protocol_type} onChange={(e) => set("protocol_type", e.target.value)}
                      data-testid="wizard-type-select"
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Termin (Datum)</Label>
                <Input type="date" value={form.appointment_date || ""} onChange={(e) => set("appointment_date", e.target.value)} data-testid="wizard-appt-date" />
              </div>
              <div><Label>Uhrzeit</Label>
                <Input type="time" value={form.appointment_time || ""} onChange={(e) => set("appointment_time", e.target.value)} data-testid="wizard-appt-time" />
              </div>
              <div><Label>Ort</Label>
                <Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="z. B. Musterstr. 1, Wohnung EG rechts" />
              </div>
            </div>
            <div>
              <Label>Beteiligte</Label>
              <div className="space-y-2 mt-1">
                {form.participants.map((p, idx) => (
                  <ParticipantRow
                    key={idx}
                    idx={idx}
                    participant={p}
                    tenancyId={form.tenancy_id}
                    onChange={(next) => {
                      const list = [...form.participants]; list[idx] = next; set("participants", list);
                    }}
                    onRemove={() => set("participants", form.participants.filter((_, i) => i !== idx))}
                  />
                ))}
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => {
                  set("participants", [...form.participants, { role: "tenant", name: "" }]);
                }}><Plus size={12} /> Beteiligten hinzufügen</Button>
              </div>
            </div>
            <TenancyLink form={form} set={set} locked={lockTenancy} />
          </div>
        )}

        {step > 1 && activeSteps[step - 1] === "Räume & Mängel" && (
          <RoomsStep form={form} set={set} />
        )}
        {step > 1 && activeSteps[step - 1] === "Zählerstände" && (
          <MetersStep form={form} set={set} />
        )}
        {step > 1 && activeSteps[step - 1] === "Schlüssel & Dokumente" && (
          <KeysDocsStep form={form} set={set} />
        )}
        {step > 1 && activeSteps[step - 1] === "Unterschriften & Abschluss" && (
          <SignaturesStep form={form} set={set} protocolId={protocolId} onSaved={onSaved} />
        )}

        <div className="flex justify-between items-center pt-4 border-t border-slate-200 mt-4">
          <div className="flex gap-2">
            {step > 1 && (
              <Button type="button" variant="outline" size="sm" onClick={() => setStep(step - 1)} className="gap-1" data-testid="wizard-prev">
                <ArrowLeft size={14} /> Zurück
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={saveDraft} disabled={saving} className="gap-1" data-testid="wizard-save-draft">
              <FloppyDisk size={14} /> Entwurf speichern
            </Button>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={openPdfInNewTab} className="gap-1" data-testid="wizard-print-pdf">
              <Printer size={14} /> Zum Ausdrucken (PDF)
            </Button>
            {canProceed ? (
              <Button type="button" size="sm" onClick={() => setStep(step + 1)} className="bg-slate-900 hover:bg-slate-800 gap-1" data-testid="wizard-next">
                Weiter <ArrowRight size={14} />
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={finalize} disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="wizard-finalize">
                <Signature size={14} /> Finalisieren (SHA-256)
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Label({ children }) {
  return <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{children}</div>;
}

function ParticipantRow({ idx, participant, tenancyId, onChange, onRemove }) {
  const [tenantSuggestions, setTenantSuggestions] = useState([]);
  const [managerSuggestions, setManagerSuggestions] = useState([]);
  const [landlordSuggestions, setLandlordSuggestions] = useState([]);

  // 2026-02 — Rollen-basierte Vorschläge: Mieter aus Mietverhältnis,
  // Verwalter aus User-Konten, Vermieter aus Objekt-Eigentümer.
  useEffect(() => {
    let cancel = false;
    (async () => {
      // Verwaltung: alle aktiven User des Mandanten (Staff-Rollen)
      try {
        const { data } = await api.get("/users");
        if (cancel) return;
        const names = (data || [])
          .filter((u) => !u.disabled)
          .map((u) => (u.full_name || u.name || u.email || "").trim())
          .filter(Boolean);
        setManagerSuggestions(Array.from(new Set(names)));
      } catch { /* silent */ }
      if (tenancyId) {
        try {
          const { data: t } = await api.get(`/tenancies/${tenancyId}`);
          if (cancel) return;
          const tenants = (t?.tenant_names || t?.tenants || []).map((x) => (
            typeof x === "string" ? x : `${x.first_name || ""} ${x.last_name || ""}`.trim()
          )).filter(Boolean);
          setTenantSuggestions(tenants);
          // Vermieter (Property owner) nachladen
          if (t?.property_id) {
            try {
              const { data: prop } = await api.get(`/properties/${t.property_id}`);
              if (cancel) return;
              const owners = [];
              if (prop?.owner_contact_id) {
                try {
                  const { data: oc } = await api.get(`/contacts/${prop.owner_contact_id}`);
                  const nm = oc?.contact_type === "person"
                    ? `${oc.first_name || ""} ${oc.last_name || ""}`.trim()
                    : (oc?.company_name || "");
                  if (nm) owners.push(nm);
                } catch { /* silent */ }
              }
              setLandlordSuggestions(owners);
            } catch { /* silent */ }
          }
        } catch { /* silent */ }
      }
    })();
    return () => { cancel = true; };
  }, [tenancyId]);

  const suggestionsFor = (role) => {
    if (role === "tenant") return tenantSuggestions;
    if (role === "manager") return managerSuggestions;
    if (role === "landlord") return landlordSuggestions;
    return [];
  };
  const suggestions = suggestionsFor(participant.role);
  const listId = `participant-suggestions-${idx}-${participant.role}`;

  return (
    <div className="flex gap-2 items-center">
      <select value={participant.role} onChange={(e) => onChange({ ...participant, role: e.target.value, name: "" })}
              className="h-10 rounded-md border border-slate-200 bg-white px-2 text-sm w-40"
              data-testid={`wizard-participant-role-${idx}`}>
        <option value="manager">Verwaltung</option>
        <option value="landlord">Vermieter</option>
        <option value="tenant">Mieter</option>
        <option value="witness">Zeuge</option>
        <option value="contractor">Handwerker</option>
        <option value="other">Sonstige</option>
      </select>
      {suggestions.length > 0 ? (
        <>
          <Input value={participant.name} placeholder="Vor- und Nachname"
                 list={listId}
                 onChange={(e) => onChange({ ...participant, name: e.target.value })}
                 data-testid={`wizard-participant-name-${idx}`} />
          <datalist id={listId}>
            {suggestions.map((s, i) => (<option key={i} value={s} />))}
          </datalist>
        </>
      ) : (
        <Input value={participant.name} placeholder="Vor- und Nachname"
               onChange={(e) => onChange({ ...participant, name: e.target.value })}
               data-testid={`wizard-participant-name-${idx}`} />
      )}
      <Button type="button" variant="ghost" size="icon" onClick={onRemove}
              data-testid={`wizard-participant-remove-${idx}`}>
        <Trash size={14} />
      </Button>
    </div>
  );
}

function TenancyLink({ form, set, locked }) {
  const [tenancies, setTenancies] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  useEffect(() => {
    api.get("/tenancies?limit=200").then(({ data }) => {
      setTenancies(data || []);
      if (locked && form.tenancy_id) {
        const t = (data || []).find((x) => x.id === form.tenancy_id);
        if (t) {
          const names = (t.tenant_names || []).join(", ") || "(unbekannt)";
          setSelectedName(`${t.contract_number} — ${names}`);
        }
      }
    }).catch(() => setTenancies([]));
  }, [locked, form.tenancy_id]);
  if (locked) {
    return (
      <div>
        <Label>Verknüpftes Mietverhältnis</Label>
        <div className="h-10 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm flex items-center text-slate-700"
             data-testid="wizard-tenancy-locked">
          <span className="font-medium">{selectedName || "lade…"}</span>
          <span className="ml-auto text-[10px] text-slate-400 italic">
            aus Mietverhältnis erstellt – nicht änderbar
          </span>
        </div>
      </div>
    );
  }
  return (
    <div>
      <Label>Verknüpftes Mietverhältnis (optional)</Label>
      <select value={form.tenancy_id || ""} onChange={(e) => set("tenancy_id", e.target.value || null)}
              data-testid="wizard-tenancy-select"
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
        <option value="">— kein Mietverhältnis (freies Protokoll) —</option>
        {tenancies.map((t) => (
          <option key={t.id} value={t.id}>
            {t.contract_number} — {(t.tenant_names || []).join(", ") || "(unbekannt)"}
          </option>
        ))}
      </select>
    </div>
  );
}

function RoomsStep({ form, set }) {
  const [unitRooms, setUnitRooms] = useState([]);
  const [loadedUnit, setLoadedUnit] = useState(null);

  useEffect(() => {
    const uid = form.unit_id;
    if (!uid || uid === loadedUnit) return;
    setLoadedUnit(uid);
    api.get(`/units/${uid}/rooms`).then(({ data }) => setUnitRooms(data || []))
       .catch(() => setUnitRooms([]));
  }, [form.unit_id, loadedUnit]);

  const addRoom = () => set("rooms", [...(form.rooms || []), { name: "", condition_notes: "", photos_base64: [], defects: [] }]);
  const upd = (idx, patch) => {
    const list = [...(form.rooms || [])]; list[idx] = { ...list[idx], ...patch }; set("rooms", list);
  };
  const rm = (idx) => set("rooms", form.rooms.filter((_, i) => i !== idx));

  const loadFromUnit = () => {
    if (!unitRooms.length) return;
    // Duplikate vermeiden: nur nicht bereits verknüpfte übernehmen
    const linked = new Set((form.rooms || []).map((r) => r.linked_room_id).filter(Boolean));
    const additions = unitRooms
      .filter((u) => !linked.has(u.id))
      .map((u) => ({
        name: u.label,
        linked_room_id: u.id,
        condition_notes: buildEquipmentSuggestion(u),
        photos_base64: [],
        defects: [],
      }));
    if (!additions.length) {
      toast.info("Alle Räume der Einheit sind bereits im Protokoll.");
      return;
    }
    set("rooms", [...(form.rooms || []), ...additions]);
    toast.success(`${additions.length} Raum${additions.length === 1 ? "" : "e"} aus Einheit übernommen`);
  };

  return (
    <div className="space-y-3">
      {form.unit_id && unitRooms.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start justify-between gap-3" data-testid="rooms-from-unit-panel">
          <div className="text-xs text-emerald-900 min-w-0">
            <div className="font-semibold">Räume aus Einheit verfügbar ({unitRooms.length})</div>
            <div className="text-[11px] mt-0.5">
              Ausstattungsmerkmale werden als Zustands-Vorschläge übernommen und als „Bestand" markiert.
              Der Verwalter kann Merkmale bei Bedarf abwählen oder anpassen.
            </div>
          </div>
          <Button type="button" size="sm" onClick={loadFromUnit}
                  className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
                  data-testid="rooms-from-unit-btn">
            Übernehmen
          </Button>
        </div>
      )}
      {(form.rooms || []).map((r, ridx) => (
        <div key={ridx} className={`border rounded-md p-3 space-y-2 ${r.linked_room_id ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-slate-50/40"}`}>
          {r.linked_room_id && (
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-600" />
              aus Einheit vorbelegt
            </div>
          )}
          <div className="flex gap-2 items-center">
            <Input value={r.name} placeholder="Raumbezeichnung (z. B. Küche, Bad, Wohnzimmer)"
                   onChange={(e) => upd(ridx, { name: e.target.value })} data-testid={`room-name-${ridx}`} />
            <Button type="button" variant="ghost" size="icon" onClick={() => rm(ridx)}><Trash size={14} /></Button>
          </div>
          <Textarea rows={r.linked_room_id ? 5 : 2} value={r.condition_notes || ""} placeholder="Zustandsbeschreibung"
                    onChange={(e) => upd(ridx, { condition_notes: e.target.value })}
                    data-testid={`room-notes-${ridx}`} />
          <PhotoUploader label="Fotos (optional, mehrere möglich)" value={r.photos_base64 || []}
                         onChange={(v) => upd(ridx, { photos_base64: v })}
                         testid={`room-photos-${ridx}`} />
          <DefectsSection defects={r.defects || []} onChange={(v) => upd(ridx, { defects: v })} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addRoom} className="gap-1"
              data-testid="wizard-add-room">
        <Plus size={12} /> Raum hinzufügen
      </Button>
    </div>
  );
}

/**
 * Erzeugt aus einem unit_rooms-Datensatz einen deutschsprachigen
 * Zustands-Vorschlag mit allen aktivierten Ausstattungsmerkmalen. Der
 * Verwalter kann den Text nach Belieben bearbeiten oder Zeilen löschen.
 */
function buildEquipmentSuggestion(u) {
  const lines = [];
  if (u.area_sqm) lines.push(`Fläche: ${u.area_sqm} m²`);
  if (u.flooring) lines.push(`Bodenbelag: ${u.flooring}`);
  if (u.wall_finish) lines.push(`Wände: ${u.wall_finish}`);
  if (u.heating_type) lines.push(`Heizung: ${u.heating_type}`);
  if (u.window_count) lines.push(`Fenster: ${u.window_count}${u.window_type ? ` · ${u.window_type}` : ""}`);
  const bestand = [];
  if (u.connections) {
    const c = u.connections;
    if (c.sockets_count) bestand.push(`${c.sockets_count} Steckdosen`);
    if (c.switches_count) bestand.push(`${c.switches_count} Lichtschalter`);
    if (c.tv) bestand.push("TV-Anschluss");
    if (c.phone_dsl) bestand.push("Telefon/DSL");
    if (c.ethernet) bestand.push("Netzwerk (RJ45)");
    if (c.water_cold) bestand.push("Wasseranschluss kalt");
    if (c.water_warm) bestand.push("Wasseranschluss warm");
    if (c.washing_machine) bestand.push("Waschmaschinen-Anschluss");
    if (c.dishwasher) bestand.push("Spülmaschinen-Anschluss");
  }
  if (u.sanitary) {
    const s = u.sanitary;
    if (s.bathtub) bestand.push("Badewanne");
    if (s.shower) bestand.push("Dusche");
    if (s.wc) bestand.push("WC");
    if (s.sink) bestand.push("Waschbecken");
    if (s.bidet) bestand.push("Bidet");
    if (s.handwash_basin) bestand.push("Handwaschbecken");
  }
  if (u.kitchen) {
    const k = u.kitchen;
    if (k.stove) bestand.push("Herd");
    if (k.oven) bestand.push("Backofen");
    if (k.hood) bestand.push("Dunstabzug");
    if (k.fridge_niche) bestand.push("Kühlschrank-Nische");
    if (k.fitted_kitchen) bestand.push("Einbauküche");
  }
  if (u.security) {
    const sec = u.security;
    if (sec.smoke_detector) bestand.push("Rauchmelder");
    if (sec.lockable_windows) bestand.push("Fenster abschließbar");
    if (sec.roller_shutters) bestand.push("Rollläden");
    if (sec.balcony_door) bestand.push("Balkontür");
  }
  if (bestand.length) lines.push(`Ausstattung (Bestand): ${bestand.join(", ")}`);
  if (u.special_features) lines.push(`Besonderheiten: ${u.special_features}`);
  if (u.condition_notes) lines.push(`Bisherige Notiz: ${u.condition_notes}`);
  return lines.join("\n");
}

function DefectsSection({ defects, onChange }) {
  const add = () => onChange([...defects, { position: "", description: "", condition: "damage", repair_needed: true, cost_estimate_eur: null }]);
  const upd = (idx, patch) => { const l = [...defects]; l[idx] = { ...l[idx], ...patch }; onChange(l); };
  const rm = (idx) => onChange(defects.filter((_, i) => i !== idx));
  return (
    <div className="pt-1">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Mängel</div>
      {defects.map((d, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-1.5 mb-1.5 items-center">
          <Input className="sm:col-span-3" value={d.position} placeholder="Position" onChange={(e) => upd(i, { position: e.target.value })} />
          <Input className="sm:col-span-4" value={d.description} placeholder="Beschreibung" onChange={(e) => upd(i, { description: e.target.value })} />
          <select className="sm:col-span-2 h-10 rounded-md border border-slate-200 bg-white px-2 text-sm"
                  value={d.condition} onChange={(e) => upd(i, { condition: e.target.value })}>
            <option value="ok">OK</option>
            <option value="wear">Abnutzung</option>
            <option value="damage">Schaden</option>
            <option value="missing">Fehlt</option>
          </select>
          <label className="sm:col-span-1 flex items-center gap-1 text-xs">
            <input type="checkbox" checked={!!d.repair_needed} onChange={(e) => upd(i, { repair_needed: e.target.checked })} /> Rep.
          </label>
          <Input className="sm:col-span-1" type="number" placeholder="€" value={d.cost_estimate_eur ?? ""} onChange={(e) => upd(i, { cost_estimate_eur: e.target.value ? Number(e.target.value) : null })} />
          <Button type="button" variant="ghost" size="icon" className="sm:col-span-1" onClick={() => rm(i)}><Trash size={12} /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add}><Plus size={12} /> Mangel hinzufügen</Button>
    </div>
  );
}

function MetersStep({ form, set }) {
  const add = () => set("meters", [...(form.meters || []), { kind: "electricity", label: "", meter_number: "", reading: "", unit: "kWh", change_type: "reading" }]);
  const addInstalled = () => set("meters", [...(form.meters || []), { kind: "electricity", label: "", meter_number: "", new_meter_number: "", reading: "", unit: "kWh", change_type: "installed" }]);
  const upd = (idx, patch) => { const l = [...(form.meters || [])]; l[idx] = { ...l[idx], ...patch }; set("meters", l); };
  const rm = (idx) => set("meters", form.meters.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2">
      <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 text-[11px] text-slate-600 leading-snug">
        <strong>Zähler-Lebenszyklus:</strong> Setzen Sie <em>Ausbau</em>, damit der alte Zähler mit Endstand in den Stammdaten deaktiviert wird.
        Für den Ersatzzähler klicken Sie unten „<em>Zähler-Einbau</em>": der neue Zähler wird nach Signatur der Einheit zugeordnet.
      </div>
      {(form.meters || []).map((m, idx) => {
        const isInstalled = m.change_type === "installed";
        const isRemoved = m.change_type === "removed";
        return (
          <div key={idx} className={`border rounded-md p-3 space-y-2 ${isInstalled ? "border-blue-200 bg-blue-50/40" : isRemoved ? "border-amber-200 bg-amber-50/40" : m.linked ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/40"}`}>
            {(m.linked || isInstalled || isRemoved) && (
              <div className={`text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 ${isInstalled ? "text-blue-700" : isRemoved ? "text-amber-800" : "text-emerald-700"}`}>
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${isInstalled ? "bg-blue-600" : isRemoved ? "bg-amber-600" : "bg-emerald-600"}`} />
                {isInstalled ? "Einbau — neuer Zähler" : isRemoved ? "Ausbau — Endstand" : "aus Objektstammdaten vorbelegt"}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
              <select className="sm:col-span-2 h-10 rounded-md border border-slate-200 bg-white px-2 text-sm"
                      value={m.kind} onChange={(e) => {
                        const unit = e.target.value === "gas" ? "m³" : e.target.value === "water_cold" || e.target.value === "water_warm" ? "m³" : "kWh";
                        upd(idx, { kind: e.target.value, unit });
                      }}>
                <option value="electricity">Strom</option>
                <option value="gas">Gas</option>
                <option value="water_cold">Kaltwasser</option>
                <option value="water_warm">Warmwasser</option>
                <option value="heating">Heizung</option>
                <option value="district_heating">Fernwärme</option>
                <option value="other">Sonstiger</option>
              </select>
              <Input className="sm:col-span-3" placeholder="Bezeichnung (z. B. HT/NT)" value={m.label || ""} onChange={(e) => upd(idx, { label: e.target.value })} data-testid={`meter-label-${idx}`} />
              <Input className="sm:col-span-3" placeholder={isInstalled ? "Alte Zähler-Nr (optional)" : "Zähler-Nummer"} value={m.meter_number || ""} onChange={(e) => upd(idx, { meter_number: e.target.value })} data-testid={`meter-number-${idx}`} />
              <Input className="sm:col-span-2" placeholder={isRemoved ? "Endstand" : (isInstalled ? "Anfangsstand" : "Zählerstand")} value={m.reading || ""} onChange={(e) => upd(idx, { reading: e.target.value })} data-testid={`meter-reading-${idx}`} />
              <Input className="sm:col-span-1" placeholder="Einheit" value={m.unit || ""} onChange={(e) => upd(idx, { unit: e.target.value })} />
              <Button type="button" variant="ghost" size="icon" className="sm:col-span-1" onClick={() => rm(idx)} data-testid={`meter-remove-${idx}`}><Trash size={14} /></Button>
            </div>
            {isInstalled && (
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                <Input className="sm:col-span-6" placeholder="Neue Zähler-Nummer *"
                       value={m.new_meter_number || ""}
                       onChange={(e) => upd(idx, { new_meter_number: e.target.value })}
                       data-testid={`meter-new-number-${idx}`} />
                <Input className="sm:col-span-6" placeholder='Grund (z. B. defekt, Turnuswechsel)'
                       value={m.change_reason || ""}
                       onChange={(e) => upd(idx, { change_reason: e.target.value })} />
              </div>
            )}
            {isRemoved && (
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                <Input className="sm:col-span-12" placeholder='Grund für den Ausbau (z. B. defekt, Turnuswechsel)'
                       value={m.change_reason || ""}
                       onChange={(e) => upd(idx, { change_reason: e.target.value })} />
              </div>
            )}
            <div className="flex items-center gap-3 text-[11px]">
              <label className="flex items-center gap-1">
                <input type="radio" checked={!m.change_type || m.change_type === "reading"}
                       onChange={() => upd(idx, { change_type: "reading" })}
                       data-testid={`meter-ct-reading-${idx}`} />
                Nur Ablesung
              </label>
              <label className="flex items-center gap-1">
                <input type="radio" checked={m.change_type === "removed"}
                       onChange={() => upd(idx, { change_type: "removed" })}
                       data-testid={`meter-ct-removed-${idx}`} />
                Ausbau (Endstand)
              </label>
              <label className="flex items-center gap-1">
                <input type="radio" checked={m.change_type === "installed"}
                       onChange={() => upd(idx, { change_type: "installed" })}
                       data-testid={`meter-ct-installed-${idx}`} />
                Einbau (neuer Zähler)
              </label>
            </div>
            <PhotoUploader label="Foto des Zählers (optional)" value={m.photo_base64 ? [m.photo_base64] : []}
                           onChange={(v) => upd(idx, { photo_base64: v[0] || null })} single testid={`meter-photo-${idx}`} />
          </div>
        );
      })}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add}
                data-testid="wizard-add-meter"><Plus size={12} /> Zähler hinzufügen</Button>
        <Button type="button" variant="outline" size="sm" className="gap-1 border-blue-200 text-blue-800 hover:bg-blue-50" onClick={addInstalled}
                data-testid="wizard-add-installed-meter"><Plus size={12} /> Zähler-Einbau</Button>
      </div>
    </div>
  );
}

function KeysDocsStep({ form, set }) {
  const addKey = () => set("keys", [...(form.keys || []), { label: "", quantity: 1, handed_over: true, notes: "" }]);
  const updKey = (i, p) => { const l = [...(form.keys || [])]; l[i] = { ...l[i], ...p }; set("keys", l); };
  const rmKey = (i) => set("keys", form.keys.filter((_, j) => j !== i));
  const addDoc = () => set("documents_transferred", [...(form.documents_transferred || []), { label: "", handed_over: true, notes: "" }]);
  const updDoc = (i, p) => { const l = [...(form.documents_transferred || [])]; l[i] = { ...l[i], ...p }; set("documents_transferred", l); };
  const rmDoc = (i) => set("documents_transferred", form.documents_transferred.filter((_, j) => j !== i));
  return (
    <div className="space-y-4">
      {HAS_KEYS.has(form.protocol_type) && (
        <div>
          <Label>Schlüssel</Label>
          {(form.keys || []).map((k, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-1.5">
              <Input className="sm:col-span-4" placeholder="Bezeichnung (z. B. Haustür, Wohnungstür)" value={k.label} onChange={(e) => updKey(i, { label: e.target.value })} />
              <Input className="sm:col-span-2" type="number" placeholder="Anzahl" value={k.quantity} onChange={(e) => updKey(i, { quantity: Number(e.target.value) })} />
              <label className="sm:col-span-2 flex items-center gap-1 text-xs">
                <input type="checkbox" checked={!!k.handed_over} onChange={(e) => updKey(i, { handed_over: e.target.checked })} /> Übergeben
              </label>
              <Input className="sm:col-span-3" placeholder="Notiz" value={k.notes} onChange={(e) => updKey(i, { notes: e.target.value })} />
              <Button type="button" variant="ghost" size="icon" className="sm:col-span-1" onClick={() => rmKey(i)}><Trash size={12} /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addKey} data-testid="wizard-add-key"><Plus size={12} /> Schlüssel hinzufügen</Button>
        </div>
      )}
      {HAS_DOCS.has(form.protocol_type) && (
        <div>
          <Label>Übergebene Dokumente</Label>
          {(form.documents_transferred || []).map((d, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-1.5">
              <Input className="sm:col-span-6" placeholder="Dokumentname" value={d.label} onChange={(e) => updDoc(i, { label: e.target.value })} />
              <label className="sm:col-span-2 flex items-center gap-1 text-xs">
                <input type="checkbox" checked={!!d.handed_over} onChange={(e) => updDoc(i, { handed_over: e.target.checked })} /> Übergeben
              </label>
              <Input className="sm:col-span-3" placeholder="Notiz" value={d.notes} onChange={(e) => updDoc(i, { notes: e.target.value })} />
              <Button type="button" variant="ghost" size="icon" className="sm:col-span-1" onClick={() => rmDoc(i)}><Trash size={12} /></Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addDoc}><Plus size={12} /> Dokument hinzufügen</Button>
        </div>
      )}
    </div>
  );
}

function SignaturesStep({ form, set, protocolId, onSaved }) {
  const [syncing, setSyncing] = useState(false);
  const setSig = (idx, sig) => {
    const list = [...(form.participants || [])];
    list[idx] = { ...list[idx], signature_png_base64: sig, signed_at: sig ? new Date().toISOString() : null };
    set("participants", list);
  };
  const syncRoomsToUnit = async () => {
    if (!protocolId) {
      toast.error("Protokoll zuerst speichern.");
      return;
    }
    if (!form.unit_id) {
      toast.error("Protokoll ist keiner Einheit zugeordnet.");
      return;
    }
    if (!window.confirm("Neue Räume und Änderungen aus diesem Protokoll in die Einheit übernehmen? Bestehende Räume werden nicht gelöscht.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/protocols/${protocolId}/sync-rooms-to-unit`);
      toast.success(`Übernahme abgeschlossen — ${data.created} neu, ${data.updated} aktualisiert, ${data.photos_added} Fotos.`);
      onSaved?.(protocolId, { rooms_synced: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };
  const canSync = form.unit_id && (form.rooms || []).length > 0;
  return (
    <div className="space-y-4">
      <div>
        <Label>Ergänzende Bemerkungen (optional)</Label>
        <Textarea rows={3} value={form.general_notes || ""} onChange={(e) => set("general_notes", e.target.value)} />
      </div>
      {canSync && (
        <div className="bg-teal-50 border border-teal-200 rounded-md p-3 flex items-start justify-between gap-3" data-testid="rooms-sync-panel">
          <div className="text-xs text-teal-900 min-w-0">
            <div className="font-semibold">Räume in Einheit zurückschreiben</div>
            <div className="text-[11px] mt-0.5">
              Neu erfasste Räume werden angelegt, Notizen und Fotos zu bestehenden
              Räumen (via „aus Einheit vorbelegt") werden ergänzt. Bestehende
              Räume in der Einheit werden NICHT gelöscht.
            </div>
          </div>
          <Button type="button" size="sm" onClick={syncRoomsToUnit} disabled={syncing}
                  className="bg-teal-700 hover:bg-teal-800 shrink-0"
                  data-testid="rooms-sync-btn">
            {syncing ? "Übernehme…" : "In Einheit übernehmen"}
          </Button>
        </div>
      )}
      <div>
        <Label>Unterschriften</Label>
        <div className="text-[11px] text-slate-500 mb-2">
          Unterschriften können <b>digital direkt hier</b> gezeichnet werden (empfohlen auf Tablet oder Touch-Gerät) —
          oder Sie klicken &bdquo;Zum Ausdrucken (PDF)&ldquo; um analog zu unterschreiben. Beim Finalisieren wird eine
          SHA-256-Prüfsumme über den Datensatz erzeugt und im Fußbereich des PDF eingebettet.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(form.participants || []).map((p, idx) => (
            <div key={idx}>
              <div className="text-[12px] font-semibold text-slate-700 mb-1">
                {p.name || "(Name fehlt)"} · <span className="text-slate-500 font-normal">
                  {({landlord: "Vermieter", tenant: "Mieter", manager: "Verwaltung", witness: "Zeuge", contractor: "Handwerker"}[p.role] || "Beteiligt")}
                </span>
              </div>
              <SignatureCanvas value={p.signature_png_base64 || ""}
                               onSignatureChange={(sig) => setSig(idx, sig)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PhotoUploader({ label, value, onChange, single = false, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    setBusy(true);
    try {
      const files = Array.from(e.target.files || []);
      const urls = await Promise.all(files.map(fileToDataUrl));
      onChange(single ? urls.slice(0, 1) : [...(value || []), ...urls]);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="flex flex-wrap gap-2 items-center">
        {(value || []).map((src, i) => (
          <div key={i} className="relative">
            <img src={src} alt="" className="h-16 w-24 object-cover rounded border border-slate-200" />
            <button type="button" onClick={() => onChange((value || []).filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full w-4 h-4 flex items-center justify-center">
              <X size={9} />
            </button>
          </div>
        ))}
        <label className="cursor-pointer inline-flex items-center gap-1 h-16 w-24 border border-dashed border-slate-300 rounded text-[11px] text-slate-500 justify-center hover:border-slate-400"
               data-testid={testid}>
          <Camera size={14} />{busy ? "…" : "Foto"}
          <input type="file" accept="image/*" multiple={!single} className="hidden" onChange={handle} />
        </label>
      </div>
    </div>
  );
}
